import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useTemplates, useDeleteTemplate, useSaveTemplate } from '@/shared/hooks/useCloudData';
import { TemplateCard } from '@/features/templates/components/TemplateCard';
import { Search, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
const categories = ['all', 'tattoo', 'microblading', 'piercing', 'aesthetic', 'medical'];

const Templates = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const { language, t } = useLanguage();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { data: templates = [], isLoading } = useTemplates();
  const deleteTemplate = useDeleteTemplate();
  const saveTemplate = useSaveTemplate();

  const handleDuplicate = async (id: string) => {
    const tpl = templates.find(t => t.id === id);
    if (!tpl) return;
    const copy = {
      ...tpl,
      id: crypto.randomUUID(),
      name: {
        fr: `Copie de ${tpl.name.fr || tpl.name.en || ''}`,
        en: `Copy of ${tpl.name.en || tpl.name.fr || ''}`,
        es: `Copia de ${tpl.name.es || tpl.name.fr || ''}`,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await saveTemplate.mutateAsync(copy);
    toast.success(language === 'fr' ? 'Modèle dupliqué' : 'Template duplicated');
  };

  const filtered = useMemo(() => {
    return templates.filter((tpl) => {
      if (tpl.archived) return false;
      if (category !== 'all' && tpl.category !== category) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          tpl.name[language].toLowerCase().includes(q) ||
          tpl.description[language].toLowerCase().includes(q) ||
          tpl.tags.some((tag) => tag.includes(q))
        );
      }
      return true;
    });
  }, [templates, search, category, language]);

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteTemplate.mutate(deleteId, {
      onSuccess: () => {
        toast.success(language === 'fr' ? 'Modèle supprimé' : language === 'es' ? 'Plantilla eliminada' : 'Template deleted');
        setDeleteId(null);
      },
    });
  };

  return (
    <>
      <div className="tablet-container pb-28 space-y-6">
        <div className="flex items-center gap-4 pt-6">
          <button onClick={() => clientId ? navigate(`/clients/${clientId}`) : navigate('/')} className="touch-button p-3 bg-secondary rounded-xl">
            <ArrowLeft size={24} />
          </button>
          <h1 className="section-title flex-1">{t('templates.title')}</h1>
        </div>

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('templates.search')}
            className="w-full pl-12 pr-4 py-4 bg-card border-2 border-border rounded-2xl text-lg focus:border-primary focus:outline-none transition-colors"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`touch-button px-5 py-3 rounded-xl whitespace-nowrap text-base ${
                category === cat
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'bg-secondary text-secondary-foreground'
              }`}
            >
              {cat === 'all' ? t('templates.all') : t(`cat.${cat}`)}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  template={tpl}
                  onUse={(id) => navigate(`/contract/${id}${clientId ? `?client_id=${clientId}` : ''}`)}
                  onEdit={(id) => navigate(`/editor/${id}`)}
                  onDelete={(id) => setDeleteId(id)}
                  onDuplicate={(id) => handleDuplicate(id)}
                  isDefault={tpl.is_system === true}
                />
              ))}
            </div>

            {!clientId && (
              <button
                onClick={() => navigate('/editor/new')}
                className="w-full card-elevated p-6 flex items-center justify-center gap-3 text-primary font-semibold text-lg border-2 border-dashed border-primary/30 hover:border-primary/60 transition-colors"
              >
                <span className="text-2xl">+</span>
                {language === 'fr' ? 'Créer un nouveau modèle' : language === 'es' ? 'Crear nueva plantilla' : 'Create new template'}
              </button>
            )}

            {filtered.length === 0 && !isLoading && (
              <div className="text-center py-16 text-muted-foreground text-lg">
                {language === 'fr' ? 'Aucun modèle trouvé' : language === 'es' ? 'No se encontraron plantillas' : 'No templates found'}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AnimatePresence>
        {deleteId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setDeleteId(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-xl border border-border"
            >
              <h3 className="text-lg font-bold">
                {language === 'fr' ? 'Supprimer ce modèle ?' : language === 'es' ? '¿Eliminar esta plantilla?' : 'Delete this template?'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {language === 'fr' ? 'Cette action est irréversible.' : language === 'es' ? 'Esta acción es irreversible.' : 'This action cannot be undone.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="touch-button flex-1 py-3 bg-secondary rounded-xl font-semibold"
                >
                  {language === 'fr' ? 'Annuler' : language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={confirmDelete}
                  className="touch-button flex-1 py-3 bg-destructive text-destructive-foreground rounded-xl font-semibold"
                >
                  {language === 'fr' ? 'Supprimer' : language === 'es' ? 'Eliminar' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Templates;
